/**
 * Dates and times in the admin panel.
 *
 * The admin server runs on UTC, pages render there and again in the
 * browser, and the admins are in India. Pinning one display zone keeps
 * both renders identical (no hydration mismatch) and independent of
 * where the admin is deployed.
 *
 * Two kinds of value, never mixed:
 *  - MOMENTS (createdAt, lastActiveAt, audit times) — shown on the
 *    admins' clock, labelled with the zone.
 *  - CALENDAR DATES (`@db.Date`: Expense.date, RecurringExpense.startDate,
 *    lastPaidDate) — the user's day, stored as 00:00Z. Shown as stored,
 *    never time-zone converted.
 */

export const ADMIN_TIME_ZONE = "Asia/Kolkata";
export const ADMIN_ZONE_LABEL = "IST";

const DAY_MS = 86_400_000;

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ADMIN_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function zoneParts(instant: Date): Record<string, string> {
  const parts: Record<string, string> = {};
  for (const p of partsFormatter.formatToParts(instant)) parts[p.type] = p.value;
  return parts;
}

/** A moment as `YYYY-MM-DD HH:mm:ss IST`. */
export function formatAdminDateTime(value: Date | string): string {
  const p = zoneParts(toDate(value));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} ${ADMIN_ZONE_LABEL}`;
}

/** The admins' calendar day a moment falls on, `YYYY-MM-DD`. */
export function formatAdminDate(value: Date | string): string {
  const p = zoneParts(toDate(value));
  return `${p.year}-${p.month}-${p.day}`;
}

/** Chart / grouping key: the admins' day of a moment. */
export const adminDayKey = formatAdminDate;

/** A `@db.Date` value, exactly as stored. */
export function formatCalendarDate(value: Date | string): string {
  return toDate(value).toISOString().slice(0, 10);
}

/** Minutes the admin zone is ahead of UTC at `instant`. */
function zoneOffsetMinutes(instant: Date): number {
  const p = zoneParts(instant);
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** The instant a `YYYY-MM-DD` day starts in the admin zone. */
export function adminDayStartOf(ymd: string): Date {
  const utcMidnight = new Date(`${ymd}T00:00:00.000Z`);
  return new Date(utcMidnight.getTime() - zoneOffsetMinutes(utcMidnight) * 60_000);
}

/** Start of the admins' day containing `now`, shifted by `dayOffset` days. */
export function adminDayStart(now: Date, dayOffset = 0): Date {
  const today = new Date(`${formatAdminDate(now)}T00:00:00.000Z`);
  const target = new Date(today.getTime() + dayOffset * DAY_MS);
  return adminDayStartOf(target.toISOString().slice(0, 10));
}

/** Start of the admins' calendar month containing `now`. */
export function adminMonthStart(now: Date): Date {
  return adminDayStartOf(`${formatAdminDate(now).slice(0, 7)}-01`);
}
